import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { usePasswordPolicy } from '@/shared/api/passwordPolicy';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Field } from '@/shared/ui';

import { useRegister } from '../queries/useSession';
import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';

/**
 * Rejestracja przez sekretny link firmy (spec sek. 10).
 *
 * Token w adresie jest jedyną przepustką — konto powstaje **nieaktywne**
 * i czeka na admina. Dlatego ekran po sukcesie NIE loguje i nie przekierowuje,
 * tylko mówi wprost, co się stało: obietnica „zaraz wejdziesz" byłaby nieprawdą.
 */
export function RegistrationScreen() {
  const { t } = useTranslation();
  const { token = '' } = useParams<{ token: string }>();
  const describeError = useApiErrorMessage();
  const registerMutation = useRegister(token);
  // ⭐ Minimum hasła z serwera (`GET /api/auth/policy`), nie ze stałej — ta sama lekcja, co na
  //   ekranie aktywacji 2026-08-22 (front „8", host 10). Dopóki polityka nie dojedzie, formularz
  //   nie udaje, że zna próg; serwer rozstrzyga, a jego odmowa niesie `args.minLength`.
  const min = usePasswordPolicy().data?.minPasswordLength;

  const [login, setLogin] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);

  const loginError = touched && login.trim() === '' ? t('auth.validation.loginRequired') : undefined;
  const nameError =
    touched && displayName.trim() === '' ? t('auth.validation.displayNameRequired') : undefined;
  const passwordError =
    touched && min !== undefined && password.length < min
      ? t('auth.validation.passwordTooShort', { min })
      : undefined;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);

    if (
      login.trim() === '' ||
      displayName.trim() === '' ||
      (min !== undefined && password.length < min)
    ) {
      return;
    }

    registerMutation.mutate({ login: login.trim(), displayName: displayName.trim(), password });
  }

  const footer = (
    <Link to="/login">{t('auth.registration.backToLogin')}</Link>
  );

  if (registerMutation.isSuccess) {
    return (
      <AuthLayout
        title={t('auth.registration.title')}
        subtitle={t('auth.registration.subtitle')}
        footer={footer}
      >
        {/* `<output>` zamiast `<p role="status">`: to jest dosłownie WYNIK
            akcji użytkownika, a element natywny niesie tę rolę sam. */}
        <output className={styles['done']}>{t('auth.registration.done')}</output>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.registration.title')}
      subtitle={t('auth.registration.subtitle')}
      footer={footer}
    >
      <form className={styles['form']} onSubmit={onSubmit} noValidate>
        {registerMutation.isError ? (
          <div className={styles['formError']} role="alert">
            {describeError(registerMutation.error)}
          </div>
        ) : null}

        <Field
          label={t('auth.login.loginField')}
          type="email"
          value={login}
          autoComplete="username"
          onChange={(event) => setLogin(event.target.value)}
          error={loginError}
        />

        <Field
          label={t('auth.registration.displayNameField')}
          value={displayName}
          autoComplete="name"
          onChange={(event) => setDisplayName(event.target.value)}
          error={nameError}
        />

        <Field
          label={t('auth.login.passwordField')}
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={(event) => setPassword(event.target.value)}
          error={passwordError}
          {...(min !== undefined ? { hint: t('auth.validation.passwordMin', { min }) } : {})}
        />

        <Button type="submit" variant="primary" block disabled={registerMutation.isPending}>
          {registerMutation.isPending
            ? t('auth.registration.submitting')
            : t('auth.registration.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
